import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin, useScript } from '#imports'

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const retry = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  let lastError
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastError = e
      if (i < retries - 1) {
        await sleep(delay * (i + 1))
      }
    }
  }
  throw lastError
}

export default defineNuxtPlugin(() => {
  const { bucketName, clientId } = useRuntimeConfig().public
  let endpoints = []
  let endpointsPromise = null
  let endpointsFetchedAt = 0
  const CACHE_TTL = 3600000 // 1 小時快取過期時間

  // 使用 useScript 載入 FingerprintJS，bundle: true 會在建置時下載並打包腳本
  const fingerprintScript = useScript({
    src: 'https://fpjscdn.net/v3/Z3A97s4eTA8uL5uWtVyN/iife.min.js',
  }, { bundle: true, trigger: 'manual' })

  const fetchEndpoints = async () => {
    const now = Date.now()

    // 檢查快取是否過期
    if (endpointsPromise && (now - endpointsFetchedAt) < CACHE_TTL) {
      return endpointsPromise
    }

    // 重置過期的快取
    if (endpointsPromise && (now - endpointsFetchedAt) >= CACHE_TTL) {
      endpoints = []
      endpointsPromise = null
    }

    if (!bucketName || !clientId) {
      endpoints = []
      return
    }

    endpointsFetchedAt = now
    endpointsPromise = (async () => {
      try {
        const url = `https://storage.googleapis.com/${bucketName}/site_resources/${clientId}/target/target.json`
        const response = await fetch(url)
        const { host: hosts } = await response.json()
        endpoints = [...new Set(hosts.map((host) => host.replace(/fnapi\d*/, 'fp')))]
      } catch (e) {
        console.error('Failed to fetch fingerprint endpoints:', e)
        endpoints = []
        // 發生錯誤時重置，允許下次重試
        endpointsPromise = null
        endpointsFetchedAt = 0
      }
    })()

    return endpointsPromise
  }

  const getDeviceToken = async () => {
    try {
      await fetchEndpoints()
      await fingerprintScript.load()

      const fp = await retry(() =>
        window.FingerprintJS.load({
          endpoint: [...endpoints, window.FingerprintJS.defaultEndpoint],
        }),
      )

      const result = await retry(() => fp.get({ timeout: 30000 }))
      return result.visitorId
    } catch (e) {
      console.error('Failed to get device token:', e)
      return null
    }
  }

  return {
    provide: {
      getDeviceToken,
    },
  }
})